import { Button, Flex, VStack } from "@chakra-ui/react";
import React from "react";
import { RiAddLine } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as RD from "src/types/remoteData";
import { useEnrollmentPeriodContext } from "../../Edit";
import { OfferingCategoriesEmptyState } from "./OfferingCategoriesEmptyState";
import {
  OfferingCategoryCard,
  OfferingCategoryDraftCard,
} from "./OfferingCategoryCard";
import { QuickCreateOfferingCategoryButton } from "./QuickCreateOfferingCategoryButton";

export const OfferingCategoriesPanel: React.FC = () => {
  const { enrollmentPeriod } = useEnrollmentPeriodContext();
  const enrollmentPeriodId = enrollmentPeriod.id;
  const [draftKeys, setDraftKeys] = React.useState<string[]>([]);

  const categoriesQuery = FSQ.useGetOfferingCategories(
    {
      path: { enrollmentPeriodId },
    },
    [enrollmentPeriodId]
  );

  const categoriesRD = RD.useFromRestResultMemoized(categoriesQuery)
    .map(({ data }) => data ?? [])
    .mapError(RD.errorResponseToError);

  const addDraft = () => {
    setDraftKeys((current) => [...current, crypto.randomUUID()]);
  };

  const discardDraft = (draftKey: string) => {
    setDraftKeys((current) => current.filter((key) => key !== draftKey));
  };

  return (
    <RemoteDataView
      remoteData={categoriesRD}
      loading={<Loading />}
      reloading={null}
      error={(error) => <GenericError error={error} />}
    >
      {(categories) =>
        categories.length === 0 && draftKeys.length === 0 ? (
          <OfferingCategoriesEmptyState
            enrollmentPeriodId={enrollmentPeriodId}
            onAddOfferingCategory={addDraft}
          />
        ) : (
          <VStack align="stretch" gap={6}>
            {categories.map((category) => (
              <OfferingCategoryCard
                key={category.id}
                enrollmentPeriodId={enrollmentPeriodId}
                category={category}
                siblingKeys={categories
                  .filter((other) => other.id !== category.id)
                  .map((other) => other.key)}
              />
            ))}
            {draftKeys.map((draftKey) => (
              <OfferingCategoryDraftCard
                key={draftKey}
                enrollmentPeriodId={enrollmentPeriodId}
                siblingKeys={categories.map((category) => category.key)}
                onDiscard={() => discardDraft(draftKey)}
              />
            ))}
            <Flex gap={3}>
              <Button
                variant="outline"
                colorScheme="gray"
                leftIcon={<RiAddLine />}
                onClick={addDraft}
              >
                Add offering category
              </Button>
              <QuickCreateOfferingCategoryButton
                enrollmentPeriodId={enrollmentPeriodId}
                existingKeys={categories.map((category) => category.key)}
              />
            </Flex>
          </VStack>
        )
      }
    </RemoteDataView>
  );
};
